import {
  PROFILE_MAX_VISIBLE_CERTIFICATIONS_SHORT_DESKTOP,
  PROFILE_MAX_VISIBLE_SERVICES,
} from '@utils/constants';
import { Box, Tag, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { FC, useMemo } from 'react';
import { useProfileContext } from '../../Contexts/ProfileContext';
import MoreItemsButton from '../MoreItemsButton';
import { useHasServices } from './useHasServices';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import s from './Services.module.scss';

type ServiceTag = {
  key: string;
  dataHook: string;
  content: string;
};

const Services: FC<{ isMobileView?: boolean }> = ({ isMobileView }) => {
  const { t } = useTranslation();
  const { partnerProfile, services = [] } = useProfileContext();
  const { logProfilePageClick } = useProfilePageBi();
  const hasServices = useHasServices();

  const foundServices = useMemo(() => {
    const servicesList = services.flatMap((category) => category.services);
    const servicesOrder =
      partnerProfile.displayConfiguration?.servicesDisplayOrder;

    const partnerServices = (partnerProfile.services?.values || [])
      .map(({ id }) => ({
        key: id,
        dataHook: `tag-service-${id}`,
        content: servicesList.find((service) => service?.id === id)?.displayKey,
      }))
      .filter((item) => item.content && item.key) as ServiceTag[];

    if (servicesOrder && servicesOrder.length > 0) {
      return partnerServices.sort((a, b) => {
        const indexA = servicesOrder.indexOf(a.key);
        const indexB = servicesOrder.indexOf(b.key);

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        return 0;
      });
    }

    return partnerServices;
  }, [
    partnerProfile.services?.values,
    services,
    partnerProfile.displayConfiguration?.servicesDisplayOrder,
  ]);

  if (!hasServices) {
    return null;
  }

  const renderServiceTag = (tag: ServiceTag, useTinySize = false) => (
    <Tag
      className={s.tag}
      key={tag.key}
      dataHook={tag.dataHook}
      id="standard"
      removable={false}
      skin="lightOutlined"
      size={useTinySize ? 'tiny' : isMobileView ? 'medium' : 'small'}
      maxWidth={200}
    >
      <Text size="small" skin="standard" ellipsis showTooltip>
        {t(`explore.services.${tag.content}`)}
      </Text>
    </Tag>
  );

  const handleShowMoreServices = () => {
    logProfilePageClick('services show more');
  };

  const handleShowLessServices = () => {
    logProfilePageClick('services show less');
  };

  const renderMoreItemsButton = (
    maxVisibleItems: number,
    useTinySize = false,
  ) => (
    <MoreItemsButton
      items={foundServices}
      maxVisibleItems={maxVisibleItems}
      isMobileView={isMobileView}
      renderItem={(tag) => renderServiceTag(tag, useTinySize)}
      className={s.servicesContainer}
      onShowMore={handleShowMoreServices}
      onShowLess={handleShowLessServices}
    />
  );

  return (
    <Box
      direction="vertical"
      className={`${s.services} ${isMobileView ? s.mobile : ''}`}
    >
      <Text size="small">{t('profile-page-services')}</Text>
      <Box marginTop="12px" dataHook="services-tags">
        <div className={s.normalHeight}>
          {renderMoreItemsButton(PROFILE_MAX_VISIBLE_SERVICES, false)}
        </div>

        <div className={s.limitedHeight}>
          {renderMoreItemsButton(
            PROFILE_MAX_VISIBLE_CERTIFICATIONS_SHORT_DESKTOP,
            true,
          )}
        </div>
      </Box>
    </Box>
  );
};

export default Services;
